import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaCheckCircle, FaBriefcase, FaFutbol, FaGlassCheers, FaEllipsisH } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Modal from '../layouts/Modal';
import { CreateEventModel, EventType, Frequency } from '../../types/event';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import { createEvent } from '../../services/eventService';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';
import DateTimePicker from '../forms/DateTimePicker';

interface CreateEventModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

const EVENT_TYPE_ICONS: Record<Exclude<EventType, EventType.DEFAULT>, React.ReactElement> = {
  [EventType.SOCIAL]: <FaGlassCheers className="h-6 w-6" />,
  [EventType.WORK]: <FaBriefcase className="h-6 w-6" />,
  [EventType.SPORT]: <FaFutbol className="h-6 w-6" />,
  [EventType.OTHER]: <FaEllipsisH className="h-6 w-6" />,
};

export default function CreateEventModal({ open, setOpen, onSuccess }: CreateEventModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Partial<CreateEventModel>>({
    type: undefined,
    title: '',
    description: '',
    location: '',
    locationUrl: '',
    onlineLocation: false,
    startTime: undefined,
    endTime: undefined,
    isRecurring: false,
    frequency: undefined,
    recurrenceEndDate: undefined,
    maximumAttendees: undefined,
    isDatePicker: false,
    customFields: [],
    dateOptions: [],
    attendanceRequired: true,
  });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setFormData({
        type: undefined,
        title: '',
        description: '',
        location: '',
        locationUrl: '',
        onlineLocation: false,
        startTime: undefined,
        endTime: undefined,
        isRecurring: false,
        frequency: undefined,
        recurrenceEndDate: undefined,
        maximumAttendees: undefined,
        isDatePicker: false,
        customFields: [],
        dateOptions: [],
        attendanceRequired: false,
      });
    }
  }, [open]);

  const totalSteps = 4;

  return (
    <Modal open={open} setOpen={setOpen} title={<>{t('page.dashboard.events.create')}</>}>
      {/* Step Indicators */}
      <div className="mb-8 flex gap-2">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={classNames('h-2 flex-1 rounded-full transition-all', {
              'bg-primary': index <= step,
              'bg-secondary-200': index > step,
            })}
          />
        ))}
      </div>

      {/* Step Content */}
      {step === 0 && <StepChooseType formData={formData} setFormData={setFormData} setStep={setStep} />}
      {step === 1 && <StepDetails formData={formData} setFormData={setFormData} setStep={setStep} />}
      {step === 2 && <StepDateTime formData={formData} setFormData={setFormData} setStep={setStep} />}
      {step === 3 && (
        <StepAdditional
          formData={formData}
          setFormData={setFormData}
          setStep={setStep}
          setOpen={setOpen}
          onSuccess={onSuccess}
        />
      )}
    </Modal>
  );
}

interface StepProps {
  formData: Partial<CreateEventModel>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateEventModel>>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

function StepChooseType({ formData, setFormData, setStep }: StepProps) {
  const { t } = useTranslation();

  const eventTypes = [
    { type: EventType.SOCIAL, label: t('page.dashboard.event.types.social') },
    { type: EventType.WORK, label: t('page.dashboard.event.types.work') },
    { type: EventType.SPORT, label: t('page.dashboard.event.types.sport') },
    { type: EventType.OTHER, label: t('page.dashboard.event.types.other') },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-primary">{t('page.dashboard.event.steps.chooseType')}</h3>
      </div>

      <div className="flex flex-col gap-3">
        {eventTypes.map(({ type, label }) => {
          const icon = EVENT_TYPE_ICONS[type as keyof typeof EVENT_TYPE_ICONS];
          if (!icon) return null;

          return (
            <button
              key={type}
              type="button"
              onClick={() => setFormData({ ...formData, type })}
              className={classNames(
                'flex items-center gap-4 rounded-lg border-2 p-4 text-left transition-all',
                formData.type === type ? 'border-primary bg-primary/5' : 'border-secondary-200 hover:border-primary/30',
              )}>
              <div
                className={classNames(
                  'rounded-lg p-3',
                  formData.type === type ? 'bg-primary text-white' : 'bg-secondary-100 text-primary',
                )}>
                {icon}
              </div>
              <div className="flex-1">
                <p className="font-medium text-primary">{label}</p>
              </div>
              {formData.type === type && <FaCheckCircle className="h-5 w-5 text-primary" />}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <Button
          type="button"
          variant="primary"
          disabled={!formData.type}
          onClick={() => setStep(1)}
          className="px-6 py-2">
          {t('general.continue')}
        </Button>
      </div>
    </div>
  );
}

function StepDetails({ formData, setFormData, setStep }: StepProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title?.trim()) {
      newErrors.title = t('page.dashboard.event.errors.titleRequired');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setStep(2);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-primary">{t('page.dashboard.event.steps.details')}</h3>
      </div>

      <div className="flex flex-col gap-4">
        {/* Title */}
        <div>
          <label htmlFor="title" className="mb-1 block text-sm font-medium text-primary">
            {t('page.dashboard.event.title')} *
          </label>
          <input
            id="title"
            type="text"
            value={formData.title || ''}
            onChange={(e) => {
              setFormData({ ...formData, title: e.target.value });
              if (errors.title) {
                setErrors({ ...errors, title: '' });
              }
            }}
            placeholder={t('page.dashboard.event.titlePlaceholder')}
            className={classNames(
              'w-full rounded-lg border-none !bg-secondary-50 p-2 placeholder:text-primary',
              errors.title && 'ring-2 ring-red-500',
            )}
          />
          {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="mb-1 block text-sm font-medium text-primary">
            {t('page.dashboard.event.description')}
          </label>
          <textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('page.dashboard.event.descriptionPlaceholder')}
            rows={3}
            className="w-full resize-none rounded-lg border-none p-2"
          />
        </div>

        {/* Location Type */}
        <div>
          <label className="mb-2 block text-sm font-medium text-primary">
            {t('page.dashboard.event.locationType')}
          </label>
          <ToggleSwitch
            leftLabel={t('page.dashboard.event.offlineLocation')}
            rightLabel={t('page.dashboard.event.onlineLocation')}
            value={formData.onlineLocation || false}
            onChange={(value) => setFormData({ ...formData, onlineLocation: value })}
          />
        </div>

        {/* Location Input */}
        {!formData.onlineLocation ? (
          <div>
            <label htmlFor="location" className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.location')}
            </label>
            <input
              id="location"
              type="text"
              value={formData.location || ''}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={t('page.dashboard.event.locationPlaceholder')}
              className="w-full rounded-lg border-none !bg-secondary-50 p-2 placeholder:text-gray-400"
            />
          </div>
        ) : (
          <div>
            <label htmlFor="locationUrl" className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.locationUrl')}
            </label>
            <input
              id="locationUrl"
              type="text"
              value={formData.locationUrl || ''}
              onChange={(e) => setFormData({ ...formData, locationUrl: e.target.value })}
              placeholder="https://..."
              className="w-full rounded-lg border-none !bg-secondary-50 p-2 placeholder:text-gray-400"
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => setStep(0)} className="px-6 py-2">
          {t('general.back')}
        </Button>
        <Button type="button" variant="primary" onClick={handleNext} className="px-6 py-2">
          {t('general.continue')}
        </Button>
      </div>
    </div>
  );
}

function StepDateTime({ formData, setFormData, setStep }: StepProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addEndTime, setAddEndTime] = useState(!!formData.endTime);

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.startTime) {
      newErrors.startTime = t('page.dashboard.event.errors.startTimeRequired');
    }
    if (formData.endTime && formData.startTime && new Date(formData.endTime) <= new Date(formData.startTime)) {
      newErrors.endTime = t('page.dashboard.event.errors.endTimeAfterStart');
    }
    if (formData.isRecurring && !formData.frequency) {
      newErrors.frequency = t('page.dashboard.event.errors.frequencyRequired');
    }
    if (formData.isRecurring && !formData.recurrenceEndDate) {
      newErrors.recurrenceEndDate = t('page.dashboard.event.errors.recurrenceEndDateRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setStep(3);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-primary">{t('page.dashboard.event.steps.dateTime')}</h3>
      </div>

      <div className="flex flex-col gap-4">
        {/* Start Time */}
        <div>
          <label htmlFor="startTime" className="mb-1 block text-sm font-medium text-primary">
            {t('page.dashboard.event.startTime')} *
          </label>
          <DateTimePicker
            value={formData.startTime}
            onChange={(date) => {
              setFormData({ ...formData, startTime: date ?? undefined });
              if (errors.startTime) setErrors({ ...errors, startTime: '' });
            }}
            timeEnabled
            className={classNames('w-full', errors.startTime && 'ring-2 ring-red-500')}
            ariaLabel={t('page.dashboard.event.startTime')}
          />
          {errors.startTime && <p className="mt-1 text-sm text-red-500">{errors.startTime}</p>}
        </div>

        {/* Add End Time Toggle */}
        <div>
          <label className="mb-2 block text-sm font-medium text-primary">{t('page.dashboard.event.addEndTime')}</label>
          <ToggleSwitch
            leftLabel={t('general.no')}
            rightLabel={t('general.yes')}
            value={addEndTime}
            onChange={(value) => {
              setAddEndTime(value);
              if (!value) {
                setFormData({ ...formData, endTime: undefined });
              }
            }}
          />
        </div>

        {/* End Time */}
        {addEndTime && (
          <div>
            <label htmlFor="endTime" className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.endTime')}
            </label>
            <DateTimePicker
              value={formData.endTime}
              onChange={(date) => {
                setFormData({ ...formData, endTime: date ?? undefined });
                if (errors.endTime) setErrors({ ...errors, endTime: '' });
              }}
              timeEnabled
              className={classNames('w-full', errors.endTime && 'ring-2 ring-red-500')}
              ariaLabel={t('page.dashboard.event.endTime')}
            />
            {errors.endTime && <p className="mt-1 text-sm text-red-500">{errors.endTime}</p>}
          </div>
        )}

        {/* Recurring Event Toggle */}
        <div>
          <label className="mb-2 block text-sm font-medium text-primary">
            {t('page.dashboard.event.recurringEvent')}
          </label>
          <ToggleSwitch
            leftLabel={t('general.no')}
            rightLabel={t('general.yes')}
            value={formData.isRecurring || false}
            onChange={(value) => {
              if (!value) {
                setFormData({
                  ...formData,
                  isRecurring: false,
                  frequency: undefined,
                  recurrenceEndDate: undefined,
                });
              } else {
                setFormData({ ...formData, isRecurring: true });
              }
            }}
          />
        </div>

        {/* Recurring Options */}
        {formData.isRecurring && (
          <>
            <div>
              <label htmlFor="frequency" className="mb-1 block text-sm font-medium text-primary">
                {t('page.dashboard.event.frequency')} *
              </label>
              <select
                id="frequency"
                value={formData.frequency || ''}
                onChange={(e) => {
                  setFormData({ ...formData, frequency: e.target.value as Frequency });
                  if (errors.frequency) setErrors({ ...errors, frequency: '' });
                }}
                className={classNames('w-full rounded-lg border-none p-2', errors.frequency && 'ring-2 ring-red-500')}>
                <option value="">{t('page.dashboard.event.selectFrequency')}</option>
                <option value={Frequency.DAILY}>{t('page.dashboard.event.frequencies.daily')}</option>
                <option value={Frequency.WEEKLY}>{t('page.dashboard.event.frequencies.weekly')}</option>
                <option value={Frequency.BI_WEEKLY}>{t('page.dashboard.event.frequencies.biWeekly')}</option>
                <option value={Frequency.MONTHLY}>{t('page.dashboard.event.frequencies.monthly')}</option>
                <option value={Frequency.YEARLY}>{t('page.dashboard.event.frequencies.yearly')}</option>
              </select>
              {errors.frequency && <p className="mt-1 text-sm text-red-500">{errors.frequency}</p>}
            </div>

            <div>
              <label htmlFor="recurrenceEndDate" className="mb-1 block text-sm font-medium text-primary">
                {t('page.dashboard.event.recurrenceEndDate')} *
              </label>
              <DateTimePicker
                value={formData.recurrenceEndDate}
                onChange={(date) => {
                  setFormData({
                    ...formData,
                    recurrenceEndDate: date ?? undefined,
                  });
                  if (errors.recurrenceEndDate) setErrors({ ...errors, recurrenceEndDate: '' });
                }}
                dateOnly
                className={classNames('w-full', errors.recurrenceEndDate && 'ring-2 ring-red-500')}
                ariaLabel={t('page.dashboard.event.recurrenceEndDate')}
              />
              {errors.recurrenceEndDate && <p className="mt-1 text-sm text-red-500">{errors.recurrenceEndDate}</p>}
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => setStep(1)} className="px-6 py-2">
          {t('general.back')}
        </Button>
        <Button type="button" variant="primary" onClick={handleNext} className="px-6 py-2">
          {t('general.continue')}
        </Button>
      </div>
    </div>
  );
}

interface StepAdditionalProps extends StepProps {
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

function StepAdditional({ formData, setFormData, setStep, setOpen, onSuccess }: StepAdditionalProps) {
  const { t } = useTranslation();
  const selectedCommunity = useSelectedCommunity();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (formData.maximumAttendees && formData.maximumAttendees < 1) {
      newErrors.maximumAttendees = t('page.dashboard.event.errors.maximumAttendeesMin');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!selectedCommunity?.id) {
      toast.error(t('page.dashboard.event.errors.noCommunity'));
      return;
    }

    setLoading(true);

    try {
      const eventData: CreateEventModel = {
        title: formData.title!,
        description: formData.description || '',
        location: formData.location || '',
        locationUrl: formData.locationUrl,
        onlineLocation: formData.onlineLocation || false,
        startTime: formData.startTime!,
        endTime: formData.endTime,
        isRecurring: formData.isRecurring,
        frequency: formData.frequency,
        recurrenceEndDate: formData.recurrenceEndDate,
        maximumAttendees: formData.maximumAttendees,
        type: formData.type || EventType.SOCIAL,
        picture: formData.picture,
        isDatePicker: formData.isDatePicker || false,
        customFields: formData.customFields || [],
        dateOptions: formData.dateOptions || [],
        attendanceRequired: formData.attendanceRequired || true,
      };

      await createEvent(selectedCommunity.id, eventData);
      toast.success(t('page.dashboard.events.createSuccess'));
      setOpen(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to create event:', error);
      toast.error(error.response?.data?.message || t('page.dashboard.events.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-primary">{t('page.dashboard.event.steps.additional')}</h3>
      </div>

      <div className="flex flex-col gap-4">
        {/* Maximum Attendees */}
        <div>
          <label htmlFor="maximumAttendees" className="mb-1 block text-sm font-medium text-primary">
            {t('page.dashboard.event.maximumAttendees')}
          </label>
          <input
            id="maximumAttendees"
            type="number"
            min="1"
            step="1"
            value={formData.maximumAttendees || ''}
            onChange={(e) => {
              setFormData({ ...formData, maximumAttendees: e.target.value ? parseInt(e.target.value, 10) : undefined });
              if (errors.maximumAttendees) setErrors({ ...errors, maximumAttendees: '' });
            }}
            placeholder={t('page.dashboard.event.maximumAttendeesPlaceholder')}
            className={classNames(
              'w-full rounded-lg border-none !bg-secondary-50 p-2',
              errors.maximumAttendees && 'ring-2 ring-red-500',
            )}
          />
          {errors.maximumAttendees && <p className="mt-1 text-sm text-red-500">{errors.maximumAttendees}</p>}
        </div>

        {/* Attendance Required */}
        <div>
          <label className="mb-2 block text-sm font-medium text-primary">
            {t('page.dashboard.event.attendanceRequired')}
          </label>
          <ToggleSwitch
            leftLabel={t('general.no')}
            rightLabel={t('general.yes')}
            value={formData.attendanceRequired || false}
            onChange={(value) => setFormData({ ...formData, attendanceRequired: value })}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => setStep(2)} className="px-6 py-2">
          {t('general.back')}
        </Button>
        <Button type="button" variant="primary" disabled={loading} onClick={handleSubmit} className="px-6 py-2">
          {loading ? t('general.creating') : t('general.create')}
        </Button>
      </div>
    </div>
  );
}
